'use client';

// Живая локаль кабинета: смена языка НА МЕСТЕ, без навигации и перезагрузок
// (заказ владельца 2026-07-23: тексты меняются там, где стоят, шторки не закрываются,
// родные элементы не трогаются — им только приходят новые подписи).
//
// INVARIANT(scriptera): смену языка кабинета НЕ делать навигацией/перезагрузкой и не
// обвешивать обходами; в /app не заводить серверные getTranslations (живая подмена
// словаря их не видит). Причина: владелец отклонил три компромисса — критерий «как в
// нормальных приложениях». Канон: docs/NATIVE-SLIDE-PLAN.md §11 п.13, CLAUDE.md «Прочее».
// Кабинет целиком клиентский (в /app нет серверных переводов) — достаточно подменить
// словарь во вложенном NextIntlClientProvider. Адрес страницы в смене НЕ участвует:
// язык вошедшего хранится в базе (users.lang, гейт кабинета в app/layout.tsx), сюда
// приезжает начальным пропсом; префикс в URL остаётся внутренней деталью движка.
//
// Приоритеты языка (владелец 2026-07-23):
//   вошедший — база (users.lang), она главная и переписывает память браузера;
//   не вошедший с памятью — кука NEXT_LOCALE (мидлвара next-intl читает её на входе);
//   не вошедший без памяти — язык браузера (Accept-Language, тоже мидлвара).
// Кука — «память браузера»: сервер видит её ДО отрисовки (localStorage не видит —
// был бы прыжок языка после загрузки). Зеркалится здесь при входе и при смене.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NextIntlClientProvider, type AbstractIntlMessages } from 'next-intl';

export type CabLang = 'ru' | 'en';

// Оба словаря — ленивыми чанками; второй прогревается заранее, чтобы смена была
// мгновенной даже без сети. Пути статичны — бандлер режет по ним чанки.
// Каст: в словаре есть массивы (chips и т.п.) — рантайм next-intl их умеет, а строгий
// AbstractIntlMessages — нет (тот же трюк, что в i18n/request.ts через any-import).
const load = async (l: CabLang): Promise<AbstractIntlMessages> =>
  ((l === 'ru' ? await import('../../../messages/ru.json') : await import('../../../messages/en.json'))
    .default as unknown as AbstractIntlMessages);

/** Запомнить язык в куке next-intl (год): по ней мидлвара выбирает язык на входе без
 *  префикса локали — «память браузера» для невошедших. */
export function rememberLangCookie(l: CabLang): void {
  try {
    document.cookie = `NEXT_LOCALE=${l}; path=/; max-age=31536000; samesite=lax`;
  } catch {
    /* SSR / экзотика — кука не критична */
  }
}

const Ctx = createContext<{ switchTo: (l: CabLang) => Promise<void> }>({
  switchTo: async () => {},
});

/** Живое переключение языка кабинета (settings.tsx → «Внешний вид»). */
export function useLiveLocale(): { switchTo: (l: CabLang) => Promise<void> } {
  return useContext(Ctx);
}

export function CabinetLocaleLive({
  locale,
  messages,
  children,
}: {
  locale: CabLang;
  messages: AbstractIntlMessages;
  children: React.ReactNode;
}) {
  const [cur, setCur] = useState({ locale, messages });
  const curRef = useRef(cur.locale);
  // Часовой пояс — явный (пояс среды: на сервере серверный, в браузере браузера —
  // ровно прежний фолбэк-режим): без него use-intl на каждый формат даты сыпал
  // dev-ошибку ENVIRONMENT_FALLBACK (310 шт. за одну смену языка, проба 2026-07-23).
  const [tz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Кука ← база: язык кабинета определил гейт по users.lang — переписываем память
  // браузера (пришёл с английской страницы, в базе русский → после входа память ru).
  // Прогрев второго словаря — тем же эффектом.
  useEffect(() => {
    rememberLangCookie(curRef.current);
    // Сегмент адреса кабинета всегда ru (см. layout кабинета), а <html lang> ставит
    // корневой layout по сегменту — правим на язык профиля для читалок/автозаполнения.
    document.documentElement.lang = curRef.current;
    void load(curRef.current === 'ru' ? 'en' : 'ru').catch(() => {});
  }, []);

  const switchTo = useCallback(async (l: CabLang) => {
    if (l === curRef.current) return;
    const m = await load(l);
    curRef.current = l;
    rememberLangCookie(l);
    // Атрибут языка документа — для читалок/автозаполнения.
    document.documentElement.lang = l;
    // АДРЕС НЕ ТРОГАЕМ (владелец 10.09.2026): у кабинета языка в адресе больше нет
    // (`/app/*`, lib/cabinet-nav.ts). Прежняя тихая подмена приставки `/ru→/en`
    // (replaceState) расходилась с деревом маршрутов Next и роняла кабинет в
    // перемонтирование на первом же переходе после смены языка — веб-страница
    // «Аккаунта» мигала перед родной (видео 10.09, 08:05).
    setCur({ locale: l, messages: m });
  }, []);

  return (
    <Ctx.Provider value={{ switchTo }}>
      <NextIntlClientProvider locale={cur.locale} messages={cur.messages} timeZone={tz}>
        {children}
      </NextIntlClientProvider>
    </Ctx.Provider>
  );
}
